import {InvalidArgumentException} from "../../errors/Exceptions.js";


export class UrmCommand {
    #type; // one of properties of UrmCommandType
    #args; // array of arguments of command (indices of registers), max size is 3

    /**
     *
     * @param type is type of command -- it is one of the following: <code>UrmCommandType.Z</code>,
     * <code>UrmCommandType.S</code>, <code>UrmCommandType.T</code>, <code>UrmCommandType.J</code>
     * @param args {number[]} is non-sparse array of command arguments - integer numbers.
     * Length of this array must be the same with number of arguments required by given command <code>type</code>.
     */
    constructor(type, args) {
        if(!UrmCommandType.isValid(type)){ // validate type
            throw new InvalidArgumentException("type must be valid UrmCommandType");
        }
        if(Array.isArray(args)){ // validate args
            if(args.length === UrmCommandType.getNumberOfArgumentsForType(type)){
                for(let i = 0; i < args.length; i++){ // check if is not undefined, and if integer
                    if(!Number.isInteger(args[i])){
                        throw new InvalidArgumentException("Arguments of command must be integer");
                    }
                }

                switch (type){ // check arguments constraints
                    case UrmCommandType.Z:
                    case UrmCommandType.S:
                        if(args[0] < 0){
                            throw new InvalidArgumentException("args[0] must be >= 0");
                        }
                        break;
                    case UrmCommandType.T:
                        if(args[0] < 0){
                            throw new InvalidArgumentException("args[0] must be >= 0");
                        }
                        if(args[1] < 0){
                            throw new InvalidArgumentException("args[1] must be >= 0");
                        }
                        break;
                    case UrmCommandType.J:
                        if(args[0] < 0){
                            throw new InvalidArgumentException("args[0] must be >= 0");
                        }
                        if(args[1] < 0){
                            throw new InvalidArgumentException("args[1] must be >= 0");
                        }
                        if(args[2] < 1){ // it is command index (indexed from 1)
                            throw new InvalidArgumentException("args[2] must be >= 1");
                        }
                        break;
                }
            }
            else{
                throw new InvalidArgumentException(`Expected ${UrmCommandType.getNumberOfArgumentsForType(type)} arguments `
                    + `for command type ${UrmCommandType.toString(type)}, but got ${args.length}`);
            }
        }
        else{
            throw new InvalidArgumentException("args must be an array");
        }


        this.#type = type;
        this.#args = args;
    }

    get type(){
        return this.#type;
    }

    get args(){
        return this.#args;
    }

    /**
     * Finds maximum mentioned register's index through arguments of this command.
     *
     * This method is used to find maximum mentioned register's index in whole program --
     * in order to properly allocate registers in URM.
     * @return {number}
     */
    getMaximumMentionedRegisterIndex(){
        if ([UrmCommandType.Z, UrmCommandType.S].includes(this.#type)){
            return this.#args[0];
        }
        return Math.max(this.#args[0], this.#args[1]);
    }

    /**
     * Converts object to such string: "<code>UrmCommand{type, [args]}</code>"
     * @return {string}
     */
    toString(){
        return `${UrmCommandType.toString(this.#type)}(${this.#args})`;
    }
}

// ENUM of types of URM commands
export const UrmCommandType = Object.freeze({
    Z: Symbol("ZERO"),
    S: Symbol("INCREMENT"),
    T: Symbol("COPY"),
    J: Symbol("CONDITIONAL_TRANSITION"),


    isValid: (obj) => {
        return obj === UrmCommandType.Z ||
            obj === UrmCommandType.S ||
            obj === UrmCommandType.T ||
            obj === UrmCommandType.J;
    },

    /**
     *
     * @param type
     * @return {number} number of expected arguments for given command's <code>type</code>
     */
    getNumberOfArgumentsForType: (type) => {
        switch(type) {
            case UrmCommandType.Z:
            case UrmCommandType.S:
                return 1;
            case UrmCommandType.T:
                return 2;
            case UrmCommandType.J:
                return 3;
            default:
                throw new InvalidArgumentException("type must be a valid UrlCommandType object");
        }
    },

    toString: (type) => {
        switch(type) {
            case UrmCommandType.Z:
                return "Z";
            case UrmCommandType.S:
                return "S";
            case UrmCommandType.T:
                return "T";
            case UrmCommandType.J:
                return "J";
            default:
                throw new InvalidArgumentException("type must be a valid UrlCommandType object");
        }
    },

    fromString: (typeStr) => {
        switch(typeStr) {
            case "Z":
                return UrmCommandType.Z;
            case "S":
                return UrmCommandType.S;
            case "T":
                return UrmCommandType.T;
            case "J":
                return UrmCommandType.J;
            default:
                throw new InvalidArgumentException("typeStr must be one of following: Z, S, T, J");
        }
    }
});
